import { BRAND } from './brand';

/**
 * Legal operator & policy metadata — single source of truth for public legal pages.
 * Update `operatorName` and `address` before Paddle seller verification.
 */
export const LEGAL = {
  /** Shown as "Last updated" / effective date on all legal pages. */
  effectiveDate: 'June 20, 2026',
  /** Individual sole operator — not a registered company. */
  operatorType: 'individual' as const,
  /**
   * Your full legal name as on government ID / bank account (Paddle KYC).
   * Replace before go-live.
   */
  operatorName: 'Renewal Drive',
  /** Customer-facing product / trade name (not a registered company name). */
  tradeName: 'Renewal Drive',
  /** Address for legal notices and Paddle verification. */
  address: 'Gopalapuram, Kalidindi Mandal, Eluru, Andhra Pradesh, India — 521344',
  country: 'India',
  governingLaw: 'the laws of India',
  venue: 'the courts of competent jurisdiction in India',
  /** Response time for privacy/data requests (business days). */
  dsarResponseDays: 30,
  /** Minimum age to use the service. */
  minimumAge: 18,
  /** Free trial length — keep in sync with TRIAL_DAYS env. */
  trialDays: 7,
} as const;

/** Wrangler / Worker env key — `email` (default) or `full`. */
export const LEGAL_IDENTITY_DISPLAY_ENV_KEY = 'LEGAL_IDENTITY_DISPLAY';

/** How operator name and address appear on legal pages and support contact UI. */
export type LegalIdentityDisplay = 'email' | 'full';

type EnvLike = Record<string, unknown> | ((key: string) => string | undefined);

function envGetter(env?: EnvLike): (key: string) => string | undefined {
  if (!env) return () => undefined;
  return typeof env === 'function'
    ? env
    : (key: string) => {
        const v = env[key];
        return typeof v === 'string' && v.length ? v : undefined;
      };
}

/** Resolve display mode from env (default: email — contact address only). */
export function resolveLegalIdentityDisplay(env?: EnvLike): LegalIdentityDisplay {
  const raw = envGetter(env)(LEGAL_IDENTITY_DISPLAY_ENV_KEY)?.trim().toLowerCase();
  return raw === 'full' ? 'full' : 'email';
}

/** Contract party label for an individual trading under a product name. */
export function getLegalPartyLabel(display: LegalIdentityDisplay = 'email'): string {
  if (display === 'full') {
    return `${LEGAL.operatorName}, an individual trading as ${LEGAL.tradeName}`;
  }
  return BRAND.helloEmail;
}

/** Operator name for indemnification and similar legal references. */
export function getLegalOperatorReference(display: LegalIdentityDisplay = 'email'): string {
  return display === 'full' ? LEGAL.operatorName : BRAND.helloEmail;
}

/** Support contact label in settings and help UI. */
export function getSupportContactLabel(display: LegalIdentityDisplay = 'email'): string {
  return display === 'full' ? BRAND.supportEmailLabel : BRAND.helloEmail;
}
